import { MouseEvent, ReactNode, useState } from "react";
import { Theme, useMediaQuery } from "@mui/material";
import styled from "@emotion/styled";
import { ResponsiveDropdownGroup, ResponsiveDropdownItem } from "./common";
import { ResponsiveDropdownMenu } from "./ResponsiveDropdownMenu";
import { ResponsiveDropdownSheet } from "./ResponsiveDropdownSheet";

const AnchorArea = styled("span")({
  display: "inline-flex",
});

interface Props {
  anchor: ReactNode;
  items?: ResponsiveDropdownItem[];
  // Grouped content renders only as the mobile bottom sheet (the desktop Menu is
  // flat-only), so passing groups forces the sheet on every viewport.
  groups?: ResponsiveDropdownGroup[];
  mobileTitle?: string;
  id?: string;
}

export const ResponsiveDropdown = ({
  anchor,
  items,
  groups,
  mobileTitle,
  id,
}: Props) => {
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const useSheet = isSm || Boolean(groups);
  const isOpen = isModalOpen || Boolean(menuAnchor);

  const handleAnchorClick = (event: MouseEvent<HTMLElement>) => {
    if (useSheet) {
      setIsModalOpen(true);
    } else {
      setMenuAnchor(event.currentTarget);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setMenuAnchor(null);
  };

  const handleItemClick = (item: ResponsiveDropdownItem) => {
    item.onClick?.();
    handleClose();
  };

  return (
    <>
      <AnchorArea
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? id : undefined}
        onClick={handleAnchorClick}
      >
        {anchor}
      </AnchorArea>
      {useSheet ? (
        <ResponsiveDropdownSheet
          isOpen={isModalOpen}
          onClose={handleClose}
          title={mobileTitle}
          items={items}
          groups={groups}
          onItemClick={handleItemClick}
        />
      ) : (
        <ResponsiveDropdownMenu
          id={id}
          anchorEl={menuAnchor}
          onClose={handleClose}
          items={items ?? []}
          onItemClick={handleItemClick}
        />
      )}
    </>
  );
};
